import React, { useState } from 'react';
import { 
  AppBar, Toolbar, Typography, Button, IconButton, Drawer, 
  List, ListItem, ListItemIcon, ListItemText, Box, Chip, Avatar, Tooltip
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SchoolIcon from '@mui/icons-material/School';
import SubjectIcon from '@mui/icons-material/Subject';
import PersonIcon from '@mui/icons-material/Person';
import RoomIcon from '@mui/icons-material/Room';
import LinkIcon from '@mui/icons-material/Link';
import ScienceIcon from '@mui/icons-material/Science';
import CalendarViewWeekIcon from '@mui/icons-material/CalendarViewWeek';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import EventNoteIcon from '@mui/icons-material/EventNote';
import LogoutIcon from '@mui/icons-material/Logout';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Brightness6Icon from '@mui/icons-material/Brightness6';
import { useAuth } from '../contexts/AuthContext';
import { ColorModeContext } from '../index';

const NavBar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const colorMode = React.useContext(ColorModeContext);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/' },
    { text: 'Departments', icon: <SchoolIcon />, path: '/departments' },
    { text: 'Semesters', icon: <SchoolIcon />, path: '/semesters' },
    { text: 'Teachers', icon: <PersonIcon />, path: '/teachers' },
    { text: 'Subjects', icon: <SubjectIcon />, path: '/subjects' },
    { text: 'Rooms', icon: <RoomIcon />, path: '/rooms' },
    { text: 'Teacher-Subjects', icon: <LinkIcon />, path: '/teacher-subjects' },
    { text: 'Lab Teachers', icon: <ScienceIcon />, path: '/lab-teachers' },
    { text: 'Timetable', icon: <CalendarViewWeekIcon />, path: '/timetable' },
    { text: 'Generate Timetable', icon: <AutoFixHighIcon />, path: '/timetable-generator' },
    { text: 'Teacher Timetable', icon: <EventNoteIcon />, path: '/teacher-timetable' }
  ];

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={toggleDrawer(true)}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            College Timetable Creator
          </Typography>
          
          {/* Theme toggle */}
          <Tooltip
            title={
              colorMode.mode === 'light'
                ? 'Switch to soothing mode'
                : colorMode.mode === 'dim'
                ? 'Switch to dark mode'
                : 'Switch to light mode'
            }
          >
            <IconButton color="inherit" onClick={colorMode.toggleColorMode} sx={{ mr: 1 }}>
              {colorMode.mode === 'light' ? (
                <Brightness6Icon />
              ) : colorMode.mode === 'dim' ? (
                <Brightness4Icon />
              ) : (
                <Brightness7Icon />
              )}
            </IconButton>
          </Tooltip>
          
          {/* User info and logout button */}
          {currentUser && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Chip
                avatar={<Avatar>{currentUser.username.charAt(0).toUpperCase()}</Avatar>}
                label={currentUser.username}
                variant="outlined"
                sx={{ mr: 2, color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
              />
              <Tooltip title="Logout">
                <IconButton
                  color="inherit"
                  onClick={() => {
                    logout();
                    navigate('/login');
                  }}
                >
                  <LogoutIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      <AnimatePresence>
        {drawerOpen && (
          <Drawer
            anchor="left"
            open={drawerOpen}
            onClose={toggleDrawer(false)}
            variant="temporary"
            transitionDuration={300}
            PaperProps={{
              component: motion.div,
              initial: { x: -300 },
              animate: { x: 0 },
              exit: { x: -300 },
              transition: { type: "spring", stiffness: 300, damping: 30 }
            }}
          >
            <Box
              component={motion.div}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.3 }}
              sx={{ width: 250 }}
              role="presentation"
              onClick={toggleDrawer(false)}
              onKeyDown={toggleDrawer(false)}
            >
          <List>
            {menuItems.map((item) => (
              <ListItem 
                button 
                key={item.text} 
                component={Link} 
                to={item.path}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            ))}
          </List>
            </Box>
          </Drawer>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
